import type { ERROR_DATA } from 'hooks/api/type';

import { cleanSearchParams, limitText } from 'utils/common';

import { MAX_LIMIT_SYMBOL_LENGTH } from 'constants/common';

import { PROVIDER_REQUEST_MAP } from './constants';
import type { OptionsProviderProps } from './types';
import { PROVIDER_NAMES } from './types';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

export interface FilterProviderResponse<T> {
    data: T[];
}
interface UseFilterProviderOptions<T, S = FilterProviderResponse<T>> extends OptionsProviderProps {
    select?: (data: FilterProviderResponse<T>) => S;
}

/**
 * Hook for fetching data from provider for autocomplete
 * @param providerName - provider name for fetch data
 * @param value - value for search
 * @param optionsProvider - additional options for provider request
 * @returns data, isLoading, isFetched, isFetching, isError, and error status
 */
const useFilterProvider = <T, S = FilterProviderResponse<T>>(
    providerName: PROVIDER_NAMES = PROVIDER_NAMES.EMPTY,
    value = '',
    optionsProvider?: UseFilterProviderOptions<T, S>
): UseQueryResult<S, ERROR_DATA<unknown>> => {
    const { additionalParams, select } = optionsProvider ?? {};
    const limitedText = limitText(value, MAX_LIMIT_SYMBOL_LENGTH);
    const cleanedParams = cleanSearchParams(additionalParams ?? {});

    const queryFn = async (): Promise<FilterProviderResponse<T>> => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return PROVIDER_REQUEST_MAP[providerName](limitedText, cleanedParams);
    };

    return useQuery<FilterProviderResponse<T>, ERROR_DATA<unknown>, S>({
        queryKey: ['toolbar-filter', providerName, value, additionalParams],
        queryFn,
        enabled: Boolean(value) || providerName === PROVIDER_NAMES.EMPTY,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        select,
    });
};

export { useFilterProvider };
